import { Pressable, View } from 'react-native';
import { Logo } from '~/global/components/logo/Logo';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import { Text } from '@/components/ui/text';
import { SidebarState } from '~/layout/enum/SidebarState';
import { useSidebarLayout } from '~/layout/contexts/sidebar-layout/SidebarLayout';
import { useMemo } from 'react';
import { createMainMenu } from '~/layout/defs/createMainMenu';
import { useTranslation } from 'react-i18next';
import { SidebarMenuItem } from '~/layout/components/sidebar-menu-item/SidebarMenuItem';
import { usePurchases } from '~/purchases/contexts/purchases/Purchases';
import { usePaywall } from '~/purchases/hooks/usePaywall';
import { Icon } from '~/global/components/icon/Icon';
import { useRouter } from 'expo-router';
import { useColor } from '~/global/hooks/useColor';

export interface SidebarProps {
  contextBadge?: 'workout' | 'meal' | null;
}

export const Sidebar = ({ contextBadge = null }: SidebarProps) => {
  const { state, itemsDetailsVisibleState } = useSidebarLayout();
  const open = state === SidebarState.EXPANDED;
  const itemsStyle = useAnimatedStyle(() => ({
    opacity: itemsDetailsVisibleState.value,
  }));
  const { t } = useTranslation('navigation');
  const menuItems = useMemo(() => createMainMenu(t, 'sidebar'), [t]);
  // AI Coach chat is available to everyone (Free users have a monthly FYT credit
  // allotment); the Upgrade CTA only appears for users below the AI tier.
  const { hasAiCoach } = usePurchases();
  const { openPaywall } = usePaywall();
  const router = useRouter();
  const accentTextColor = useColor('accent-text');
  const mutedColor = useColor('text-secondary');

  const handleAICoach = () => {
    router.push('/chat');
  };

  return (
    <View className="bg-surface flex-1 py-6 px-4 gap-y-8">
      {/* Logo Section */}
      <View className="flex flex-row items-center gap-x-3">
        <Logo size={40} className="min-w-10 rounded-lg" />
        {open && (
          <Animated.View style={itemsStyle}>
            <Text className="text-text-primary text-[22px] font-sora-bold tracking-tight">
              fytness
            </Text>
          </Animated.View>
        )}
      </View>

      {/* Navigation */}
      <View className="gap-y-1">
        {menuItems.map((item) => (
          <SidebarMenuItem {...item} key={item.id} />
        ))}
      </View>

      {/* Spacer */}
      <View className="flex-1" />

      {/* Bottom Section - AI Coach (always available); an Upgrade CTA sits above
          it for users below the AI tier. */}
      <View className="gap-y-2">
        {!hasAiCoach && (
          <Pressable
            onPress={openPaywall}
            className="rounded-lg py-2 active:opacity-70 web:hover:bg-surface-elevated"
          >
            <View className="flex flex-row items-center justify-center gap-x-2">
              {open ? (
                <Animated.View
                  style={[
                    itemsStyle,
                    { flexDirection: 'row', gap: 8, alignItems: 'center' },
                  ]}
                >
                  <Icon
                    name="Sparkles"
                    className="w-4 h-4"
                    size={16}
                    color={mutedColor}
                  />
                  <Text
                    className="text-sm font-inter-medium"
                    style={{ color: mutedColor }}
                  >
                    {t('subscription:upgrade')}
                  </Text>
                </Animated.View>
              ) : (
                <Icon
                  name="Sparkles"
                  className="w-4 h-4"
                  size={16}
                  color={mutedColor}
                />
              )}
            </View>
          </Pressable>
        )}

        <Pressable
          onPress={handleAICoach}
          className="relative active:opacity-80"
        >
          <View
            style={{
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 1 },
              shadowOpacity: 0.06,
              shadowRadius: 8,
              elevation: 2,
            }}
            className="bg-accent rounded-xl h-[52px] px-4 py-3.5 flex flex-row items-center justify-center gap-x-3"
          >
            {open ? (
              <Animated.View
                style={[
                  itemsStyle,
                  { flexDirection: 'row', gap: 12, alignItems: 'center' },
                ]}
              >
                <Icon
                  name="Bot"
                  className="w-6 h-6"
                  size={24}
                  color={accentTextColor}
                />
                <Text
                  className="text-[15px] font-inter-semibold"
                  style={{ color: accentTextColor }}
                >
                  AI Coach
                </Text>
              </Animated.View>
            ) : (
              <Icon
                name="Bot"
                className="w-6 h-6"
                size={24}
                color={accentTextColor}
              />
            )}
          </View>
          {/* Context Badge */}
          {contextBadge && (
            <View
              style={{
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 1 },
                shadowOpacity: 0.19,
                shadowRadius: 4,
                elevation: 3,
              }}
              className="absolute -top-1.5 -right-1.5 bg-white rounded-[10px] w-5 h-5 flex items-center justify-center"
            >
              <Icon
                name={contextBadge === 'workout' ? 'Dumbbell' : 'Utensils'}
                className="text-accent-text"
                size={12}
              />
            </View>
          )}
        </Pressable>
      </View>
    </View>
  );
};
